import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowRight, Ticket, ShoppingBag, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

function StatBlock({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border p-8" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-3">
        <Icon size={16} strokeWidth={1.5} />
        <p className="label">{label}</p>
      </div>
      <p className="font-display text-5xl font-light">{value}</p>
    </div>
  );
}

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [notAffiliate, setNotAffiliate] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/affiliate/dashboard")
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err.response?.status === 404) setNotAffiliate(true);
        else toast.error("Could not load your affiliate dashboard");
      });
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.promo_code);
      setCopied(true);
      toast.success("Promo code copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please copy it manually");
    }
  };

  if (notAffiliate) {
    return (
      <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="affiliate-dashboard-page">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <FadeUp>
            <p className="label text-accent mb-6">/ Affiliate Hub</p>
            <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">Not an affiliate yet</h1>
            <p className="mt-6 text-sm text-muted-foreground max-w-md">
              There's no approved affiliate account linked to your email. Apply below — once approved, your stats will appear here.
            </p>
            <Link to="/affiliate" className="mt-8 label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-accent hover:border-accent transition-colors" data-testid="affiliate-apply-cta">
              Apply for the affiliate program <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </main>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><span className="label text-muted-foreground animate-pulse">Loading…</span></div>;
  }

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="affiliate-dashboard-page">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <FadeUp>
          <p className="label text-accent mb-6">/ Affiliate Hub</p>
          <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">
            Welcome back, {data.name?.split(" ")[0] || "partner"}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">Every order placed with your code shows up here.</p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-12 border border-border bg-secondary/40 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="affiliate-promo-card">
            <div>
              <p className="label text-muted-foreground mb-3">Your promo code</p>
              <p className="font-mono text-3xl md:text-4xl tracking-[0.2em]" data-testid="affiliate-promo-code">{data.promo_code}</p>
              <p className="mt-2 text-xs text-muted-foreground">Share this code — it gives your audience 10% off and credits every order to you.</p>
            </div>
            <button
              onClick={copyCode}
              className="label inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 hover:bg-accent hover:text-accent-foreground transition-colors shrink-0"
              data-testid="copy-promo-btn"
            >
              {copied ? <Check size={16} strokeWidth={1.5} /> : <Copy size={16} strokeWidth={1.5} />}
              {copied ? "Copied" : "Copy code"}
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="mt-8 grid md:grid-cols-2 gap-px bg-border border border-border">
            <div className="bg-background">
              <StatBlock icon={ShoppingBag} label="Total orders" value={data.total_orders} testid="stat-affiliate-orders" />
            </div>
            <div className="bg-background">
              <StatBlock icon={CircleDollarSign} label="Total revenue generated" value={`$${data.total_revenue.toFixed(2)}`} testid="stat-affiliate-revenue" />
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div className="mt-14">
            <h2 className="font-display text-3xl font-light mb-6">Referred orders</h2>
            {data.orders.length === 0 ? (
              <p className="label text-muted-foreground" data-testid="affiliate-no-orders">No orders with your code yet — share it to get started.</p>
            ) : (
              <div className="border border-border overflow-x-auto" data-testid="affiliate-orders-table">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="label text-muted-foreground text-left">
                      <th className="p-3 border-b border-border font-normal">Order</th>
                      <th className="p-3 border-b border-border font-normal">Date</th>
                      <th className="p-3 border-b border-border font-normal">Status</th>
                      <th className="p-3 border-b border-border font-normal text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.order_number} className="font-mono hover:bg-secondary/50 transition-colors" data-testid={`affiliate-order-${o.order_number}`}>
                        <td className="p-3 border-b border-border">#{o.order_number}</td>
                        <td className="p-3 border-b border-border text-muted-foreground">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </td>
                        <td className="p-3 border-b border-border"><span className="label">{o.status.replace(/_/g, " ")}</span></td>
                        <td className="p-3 border-b border-border text-right">${o.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </FadeUp>
      </div>
    </main>
  );
}
